import { clsx } from "clsx";
import type { ReactNode } from "react";

export interface IconToggleOption<T extends string> {
  value: T;
  icon: ReactNode;
  "aria-label": string;
}

export interface ViewToggleProps<T extends string> {
  options: readonly [IconToggleOption<T>, IconToggleOption<T>, ...IconToggleOption<T>[]];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  "aria-label"?: string;
}

/**
 * Icon-only segmented toggle (e.g. list view / grid view). For label-based
 * toggles, use `<SegmentedControl>`.
 */
export function ViewToggle<T extends string>({
  options,
  value,
  onChange,
  className,
  "aria-label": ariaLabel,
}: ViewToggleProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={clsx(
        "inline-flex h-10 items-center gap-1 rounded-xl border border-borderDefault px-1",
        className,
      )}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            aria-label={opt["aria-label"]}
            onClick={() => onChange(opt.value)}
            className={clsx(
              "inline-flex size-8 items-center justify-center rounded-lg transition-colors",
              selected ? "bg-accentCyanTint text-textDark" : "text-textBody hover:bg-bgLight",
            )}
          >
            {opt.icon}
          </button>
        );
      })}
    </div>
  );
}
